import React, { createContext, useContext, useState, useEffect } from 'react';

// Theme definitions
export const themes = {
  'kid-friendly': {
    name: 'Kid-Friendly',
    emoji: '🎈',
    colors: {
      primary: '#FF8C00',
      secondary: '#FFA500',
      accent: '#FFD700',
      background: 'linear-gradient(135deg, #FFE5B4, #FFB366)',
      cardBackground: 'white',
      textPrimary: '#333',
      textSecondary: '#666',
      textLight: 'rgba(255, 255, 255, 0.9)',
      border: '#FFD700',
      shadow: 'rgba(255, 140, 0, 0.15)',
      hoverShadow: 'rgba(255, 140, 0, 0.25)',
    },
    fonts: {
      primary: "'Comic Sans MS', cursive, sans-serif",
      secondary: "'Comic Sans MS', cursive, sans-serif",
    },
    borderRadius: {
      small: '12px',
      medium: '15px',
      large: '20px',
      xl: '25px',
    },
    animations: {
      bounce: 'bounce 2s infinite',
      float: 'float 20s infinite linear',
    }
  },
  
  professional: {
    name: 'Professional',
    emoji: '💼',
    colors: {
      primary: '#2563eb',
      secondary: '#3b82f6',
      accent: '#1d4ed8',
      background: 'linear-gradient(135deg, #f8fafc, #e2e8f0)',
      cardBackground: 'white',
      textPrimary: '#1e293b',
      textSecondary: '#64748b',
      textLight: 'rgba(255, 255, 255, 0.9)',
      border: '#e2e8f0',
      shadow: 'rgba(0, 0, 0, 0.1)',
      hoverShadow: 'rgba(0, 0, 0, 0.15)',
    },
    fonts: {
      primary: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      secondary: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    },
    borderRadius: {
      small: '6px',
      medium: '8px',
      large: '12px',
      xl: '16px',
    },
    animations: {
      bounce: 'none',
      float: 'none',
    }
  },
  
  'rock-roll': {
    name: 'Rock & Roll',
    emoji: '🎸',
    colors: {
      primary: '#dc2626',
      secondary: '#ef4444',
      accent: '#fbbf24',
      background: 'linear-gradient(135deg, #1f2937, #374151)',
      cardBackground: '#111827',
      textPrimary: '#f9fafb',
      textSecondary: '#d1d5db',
      textLight: 'rgba(255, 255, 255, 0.9)',
      border: '#fbbf24',
      shadow: 'rgba(220, 38, 38, 0.2)',
      hoverShadow: 'rgba(220, 38, 38, 0.3)',
    },
    fonts: {
      primary: "'Bebas Neue', 'Arial Black', cursive, sans-serif",
      secondary: "'Roboto Condensed', sans-serif",
    },
    borderRadius: {
      small: '4px',
      medium: '6px',
      large: '8px',
      xl: '10px',
    },
    animations: {
      bounce: 'rockBounce 1.5s infinite',
      float: 'rockFloat 15s infinite linear',
    }
  },
  
  blue: {
    name: 'Blue',
    emoji: '💙',
    colors: {
      primary: '#1e40af',
      secondary: '#3b82f6',
      accent: '#60a5fa',
      background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
      cardBackground: 'white',
      textPrimary: '#1e3a8a',
      textSecondary: '#3730a3',
      textLight: 'rgba(255, 255, 255, 0.9)',
      border: '#93c5fd',
      shadow: 'rgba(30, 64, 175, 0.1)',
      hoverShadow: 'rgba(30, 64, 175, 0.2)',
    },
    fonts: {
      primary: "'Nunito', sans-serif",
      secondary: "'Open Sans', sans-serif",
    },
    borderRadius: {
      small: '8px',
      medium: '12px',
      large: '16px',
      xl: '20px',
    },
    animations: {
      bounce: 'gentleBounce 3s infinite',
      float: 'gentleFloat 25s infinite linear',
    }
  },
  
  romance: {
    name: 'Romance',
    emoji: '💕',
    colors: {
      primary: '#ec4899',
      secondary: '#f472b6',
      accent: '#fbbf24',
      background: 'linear-gradient(135deg, #fdf2f8, #fce7f3)',
      cardBackground: 'white',
      textPrimary: '#831843',
      textSecondary: '#be185d',
      textLight: 'rgba(255, 255, 255, 0.9)',
      border: '#f9a8d4',
      shadow: 'rgba(236, 72, 153, 0.15)',
      hoverShadow: 'rgba(236, 72, 153, 0.25)',
    },
    fonts: {
      primary: "'Dancing Script', cursive",
      secondary: "'Poppins', sans-serif",
    },
    borderRadius: {
      small: '15px',
      medium: '20px',
      large: '25px',
      xl: '30px',
    },
    animations: {
      bounce: 'heartBounce 2.5s infinite',
      float: 'romanticFloat 30s infinite linear',
    }
  }
};

// Theme Context
const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState('professional');

  // Load theme from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('memseat-theme');
    if (savedTheme && themes[savedTheme]) {
      setCurrentTheme(savedTheme);
    }
  }, []);

  // Save theme to localStorage when changed
  useEffect(() => {
    localStorage.setItem('memseat-theme', currentTheme);
    // Apply theme to document root for CSS variables
    applyThemeToDocument(themes[currentTheme]);
  }, [currentTheme]);

  const applyThemeToDocument = (theme) => {
    const root = document.documentElement;
    
    // Set CSS custom properties
    Object.entries(theme.colors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}`, value);
    });
    
    Object.entries(theme.fonts).forEach(([key, value]) => {
      root.style.setProperty(`--font-${key}`, value);
    });
    
    Object.entries(theme.borderRadius).forEach(([key, value]) => {
      root.style.setProperty(`--radius-${key}`, value);
    });
    
    Object.entries(theme.animations).forEach(([key, value]) => {
      root.style.setProperty(`--animation-${key}`, value);
    });
  };

  const changeTheme = (themeName) => {
    if (themes[themeName]) {
      setCurrentTheme(themeName);
    }
  };

  const value = {
    currentTheme,
    theme: themes[currentTheme],
    themes,
    changeTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}; 